import { Link, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useWorkspaces } from '../api/hooks';
import { useSession } from '../auth/session';
import { ThemeToggle } from '../ui/theme-toggle';
import { BidraMark } from '../ui/icons';
import { имяЭкрана, коробкаЭкрана, подписьЭкрана } from './screen';
import '../kabinet/atoms.css';

/**
 * Рамка для того, что живёт при ПРОСТРАНСТВЕ, а не при кабинете.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * 19.08.2026 ЭТО ОБЕРНУЛОСЬ ЗАМКОМ, ИЗ КОТОРОГО НЕТ ВЫХОДА.
 *
 * Пароль, двухфакторка, тариф и участники стояли по адресу
 * `/rk/:wsId/:cabinetId/...` — то есть ТРЕБОВАЛИ КАБИНЕТА. А кабинета
 * у нового человека нет: он только зарегистрировался.
 *
 * Замкнулось так. Админка пускает только со включённой двухфакторкой
 * и предлагает «Включить в настройках». Настройки — внутри кабинета.
 * Кабинета нет, и на его месте стена «Подключите кабинет Wildberries».
 * Владелец сервиса не мог войти в собственную админку, потому что для
 * этого нужно было сперва подключить чужой рекламный кабинет.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЧЕМУ ЭТО БЫЛО НЕВЕРНО ПО СУЩЕСТВУ, А НЕ ПО НЕДОСМОТРУ.
 *
 * Пароль — свойство учётной записи. Тариф, участники, подписка —
 * свойства ПРОСТРАНСТВА. Ни одно из них не принадлежит кабинету:
 * кабинетов в пространстве может быть пять, а тариф один.
 *
 * Адрес, в котором они стояли, обещал обратное — будто у каждого
 * кабинета свой пароль и свой тариф. Адрес врал, и однажды это
 * обязано было выйти боком. Вышло замком.
 *
 * ПРЕЖНИЕ АДРЕСА ОСТАВЛЕНЫ РАБОЧИМИ. Ими делятся в переписке и держат
 * в закладках; ломать их ради стройности — менять одну беду на другую.
 * ═══════════════════════════════════════════════════════════════════════
 *
 * ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ. Ни полосы достоверности данных, ни подсказки
 * про холостой ход: и то и другое — про кабинет, а его тут может
 * не быть вовсе. Полоса, которая молчит, потому что ей нечего сказать, —
 * это полоса, которую перестают читать.
 */
/** Разделы пространства. Порядок — по частоте, а не по алфавиту. */
const TABS: ReadonlyArray<{ suffix: string; title: string }> = [
  // КАБИНЕТЫ ПЕРВЫМИ. Сюда приходят чаще всего, и именно отсюда
  // подключают первый кабинет — то есть делают то, без чего
  // остальные вкладки не значат ничего.
  { suffix: '/cabinets', title: 'Кабинеты' },
  { suffix: '', title: 'Пароль и доступ' },
  { suffix: '/plan', title: 'Тариф и оплата' },
  { suffix: '/members', title: 'Участники' },
];

export function AccountShell() {
  const { wsId } = useParams();
  const { signOut } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const workspaces = useWorkspaces();

  // В адресе мусор вместо пространства — возвращаем к выбору, а не
  // показываем пустую рамку: пустой экран читается как поломка
  if (!wsId) return <Navigate to="/rk" replace />;

  const ws = workspaces.data?.find((w) => w.id === wsId);
  if (workspaces.data && !ws) {
    return (
      <div
        className="flex min-h-full flex-col items-center justify-center text-center"
        style={{ gap: 'var(--sp-3)', padding: 'var(--sp-10)' }}
      >
        <h1 style={{ fontSize: 'var(--fs-h1)', lineHeight: 'var(--lh-h1)', fontWeight: 600 }}>
          Это пространство недоступно
        </h1>
        <p className="rk-faint rk-text-sm" style={{ maxWidth: 460 }}>
          Ссылку прислали из чужого пространства или доступ отозвали.
        </p>
        <Link className="rk-button no-underline" to="/rk">
          К своим пространствам
        </Link>
      </div>
    );
  }

  const tail = location.pathname.split('/settings')[1] ?? '';
  const at = (suffix: string): boolean => tail === suffix || (suffix === '' && tail === '/');

  return (
    /* Область частиц кабинета; довод — в `layout/shell.tsx`. */
    <div data-kab className="flex h-full flex-col">
      <header className="rk-app-top">
        <Link
          to="/rk"
          className="flex shrink-0 items-center gap-2.5 no-underline"
          style={{ color: 'var(--ink)' }}
        >
          <BidraMark size={26} />
          <b style={{ letterSpacing: '-.01em' }}>БИДРА</b>
        </Link>

        <span
          className="min-w-0 shrink truncate"
          style={{ fontSize: 'var(--fs-sm)', lineHeight: 'var(--lh-sm)', color: 'var(--ink-3)' }}
        >
          Настройки{ws ? ` · ${ws.name}` : ''}
        </span>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <ThemeToggle />
          {/* ВЫХОД ОТСЮДА ЕСТЬ ВСЕГДА. Это единственная рамка, куда
              человек попадает, когда кабинета нет; тупик здесь означал бы
              тот же замок, только с другой стороны. */}
          <Link className="rk-button-quiet rk-button-sm no-underline" to="/rk">
            К кабинетам
          </Link>
          <button className="rk-button-quiet rk-button-sm" onClick={() => void signOut()}>
            Выйти
          </button>
        </div>
      </header>

      {/* ВКЛАДКИ — КНОПКАМИ И КЛАССОМ `rk-seg-on`, ровно как в админке.
          Стили полосы вкладок написаны под `button`; ссылка выглядела бы
          голым текстом, и разъехалось бы это молча.

          ═══════════════════════════════════════════════════════════
          РЯД ПРОКРУЧИВАЕТСЯ ВБОК — ЭТО НАШЛА ПРОБА, А НЕ ГЛАЗ.

          Четыре вкладки со словами «Тариф и оплата» и «Пароль
          и доступ» занимают 454 пикселя. На телефоне окно 390,
          и `rk-seg` не умеет ни переноситься (`nowrap`), ни сжиматься
          (`flex: 0 0 auto`) — значит ехала ВСЯ СТРАНИЦА, на всех
          четырёх экранах настроек сразу.

          Заметить это глазами было нельзя: страница едет вбок молча,
          и видно только то, что правый край чуть обрезан. Померила
          проба `Рубильник-настройки`: 478 против окна 390.

          Лечится так же, как ряд вкладок админки: прокрутка вбок
          с тонкой полосой эталона (`data-hscroll`). Перенос в две
          строки был бы хуже — он съедает высоту там, где её и так
          нет.
          ═══════════════════════════════════════════════════════════ */}
      <div
        className="shrink-0 overflow-x-auto"
        data-hscroll="1"
        style={{ margin: 'var(--sp-3) var(--sp-6) 0' }}
      >
        <nav className="rk-seg">
        {TABS.map((t) => (
          <button
            key={t.suffix}
            className={at(t.suffix) ? 'rk-seg-on' : undefined}
            onClick={() => navigate(`/rk/${wsId}/settings${t.suffix}`)}
          >
            {t.title}
          </button>
        ))}
        </nav>
      </div>

      {/* Поля — из замера, как и в рамке кабинета: довод в `screen.ts`. */}
      <main
        className="min-h-0 flex-1 overflow-auto"
        data-d={имяЭкрана(location.pathname)}
        data-screen-label={подписьЭкрана(location.pathname)}
        style={коробкаЭкрана(location.pathname)}
      >
        <Outlet />
      </main>
    </div>
  );
}
